/**
 * related-products.js | Grifigo Theme
 *
 * 1. If section was rendered with collection products server-side — just init the slider.
 * 2. If showing skeleton — tries to fetch from same collection first, then falls back
 *    to Shopify recommendations API.
 * 3. Handles "Add to Cart" with drawer integration (matches existing ali-atc pattern).
 */
(function () {
  'use strict';

  /* ── Utilities ──────────────────────────────────────────────────────────── */

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  /* ── Fetch from Collection ───────────────────────────────────────────────
     Fetches products from the product's own collection and builds card HTML,
     then swaps the skeleton with real product cards.
  ────────────────────────────────────────────────────────────────────────── */

  function fetchFromCollection(sectionEl) {
    var collectionHandle = sectionEl.dataset.collectionHandle;
    var productId        = parseInt(sectionEl.dataset.productId, 10);
    var limit            = parseInt(sectionEl.dataset.limit, 10) || 8;
    var sectionId        = sectionEl.dataset.sectionId;

    if (!collectionHandle) {
      /* No collection handle — fall back to recommendations API */
      fetchRecommendations(sectionEl);
      return;
    }

    /* Fetch collection products JSON */
    fetch('/collections/' + collectionHandle + '/products.json?limit=50')
      .then(function (r) {
        if (!r.ok) throw new Error('Collection fetch failed: ' + r.status);
        return r.json();
      })
      .then(function (data) {
        var products = (data.products || []).filter(function (p) {
          return p.id !== productId;
        }).slice(0, limit);

        if (products.length === 0) {
          /* No products in collection — try recommendations */
          fetchRecommendations(sectionEl);
          return;
        }

        /* Build the HTML structure matching the Liquid output */
        var html = buildSectionHTML(sectionEl, products, collectionHandle);
        sectionEl.innerHTML = html;

        /* Init slider after DOM update */
        initSlider(sectionEl, sectionId);
      })
      .catch(function (err) {
        /* If collection fetch fails, try recommendations */
        if (window.console && console.warn) {
          console.warn('[Related Products] Collection fetch failed, trying recommendations:', err);
        }
        fetchRecommendations(sectionEl);
      });
  }

  /* ── Build Section HTML from products JSON ───────────────────────────────
     Constructs the same markup structure as the Liquid template renders,
     so the JS slider and CSS work identically.
  ────────────────────────────────────────────────────────────────────────── */

  function buildSectionHTML(sectionEl, products, collectionHandle) {
    var sectionId     = sectionEl.dataset.sectionId;
    var sectionBg     = sectionEl.style.getPropertyValue('--rp-section-bg') || '#faf9f7';

    /* Reconstruct header text from existing skeleton section or fall back to defaults */
    var labelText   = (qs('.rp-label', sectionEl)  || {}).textContent  || 'Handpicked For You';
    var headingText = (qs('.rp-title', sectionEl)  || {}).textContent  || 'You May Also Like';

    var cardsHtml = products.map(function (p) {
      var variant    = p.variants && p.variants[0] || {};
      var price      = variant.price ? formatMoney(variant.price) : '';
      var compare    = variant.compare_at_price;
      var onSale     = compare && compare > variant.price;
      var salePct    = onSale ? Math.round((compare - variant.price) * 100 / compare) : 0;
      var imgSrc     = p.featured_image
        ? p.featured_image.replace(/(\.\w+)$/, '_480x480$1')
        : '';
      var imgEl      = imgSrc
        ? '<img class="rp-card-img" src="' + imgSrc + '" alt="' + escHtml(p.title) + '" loading="lazy" width="480" height="480">'
        : '<div class="rp-card-img-placeholder"><svg width="48" height="48" fill="none" stroke="currentColor" stroke-width="1.2" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg></div>';
      var badgeHtml  = onSale ? '<span class="rp-badge">-' + salePct + '%</span>' : '';
      var typeHtml   = p.product_type ? '<p class="rp-card-type">' + escHtml(p.product_type) + '</p>' : '';
      var priceHtml  = price
        ? '<div class="rp-card-price"><span class="rp-price-cur">' + price + '</span>' +
          (onSale ? '<span class="rp-price-orig">' + formatMoney(compare) + '</span>' : '') +
          '</div>'
        : '';

      var ctaHtml = '';
      if (p.available !== false) {
        if (p.variants && p.variants.length > 1) {
          ctaHtml = '<a href="/products/' + p.handle + '" class="rp-btn rp-btn-cart">Choose Options</a>';
        } else {
          ctaHtml = '<button type="button" class="rp-btn rp-btn-cart ali-atc st-atc-btn" ' +
            'data-variant-id="' + variant.id + '" ' +
            'aria-label="Add ' + escHtml(p.title) + ' to cart">' +
            '<svg width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">' +
            '<path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>' +
            '<line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>' +
            ' Add to Cart</button>';
        }
      } else {
        ctaHtml = '<span class="rp-btn rp-btn-soldout">Sold Out</span>';
      }

      return '<div class="rp-card">' +
        '<a href="/products/' + p.handle + '" class="rp-card-img-link" aria-label="' + escHtml(p.title) + '">' +
        '<div class="rp-card-img-wrap">' + imgEl + badgeHtml +
        '<div class="rp-card-overlay"><span class="rp-overlay-text">View Product</span></div>' +
        '</div></a>' +
        '<div class="rp-card-body">' + typeHtml +
        '<h3 class="rp-card-title"><a href="/products/' + p.handle + '">' + escHtml(p.title) + '</a></h3>' +
        priceHtml +
        '<div class="rp-card-actions">' + ctaHtml + '</div>' +
        '</div></div>';
    }).join('');

    var viewAllUrl = collectionHandle ? '/collections/' + collectionHandle : '/collections/all';

    return '<div class="st-container">' +
      '<div class="rp-header">' +
      '<span class="rp-label">' + escHtml(labelText) + '</span>' +
      '<h2 class="rp-title">' + escHtml(headingText) + '</h2>' +
      '</div>' +
      '<div class="rp-slider-outer" id="rp-outer-' + sectionId + '">' +
      '<div class="rp-slider-track" id="rp-track-' + sectionId + '">' +
      cardsHtml +
      '</div>' +
      '<button class="rp-nav rp-nav-prev" id="rp-prev-' + sectionId + '" aria-label="Previous products">' +
      '<svg width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>' +
      '</button>' +
      '<button class="rp-nav rp-nav-next" id="rp-next-' + sectionId + '" aria-label="Next products">' +
      '<svg width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"/></svg>' +
      '</button>' +
      '</div>' +
      '<div class="rp-dots" id="rp-dots-' + sectionId + '" aria-label="Slider navigation" role="tablist"></div>' +
      '</div>';
  }

  /* ── Fetch Recommendations (Shopify API fallback) ────────────────────────
     Used when no collection handle is available or collection fetch fails.
  ────────────────────────────────────────────────────────────────────────── */

  function fetchRecommendations(sectionEl) {
    var sectionId = sectionEl.dataset.sectionId;
    var productId = sectionEl.dataset.productId;
    var limit     = sectionEl.dataset.limit || '8';

    if (!sectionId || !productId) return;

    var url =
      '/recommendations/products?section_id=' + sectionId +
      '&product_id=' + productId +
      '&limit=' + limit;

    fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error('Recommendations fetch failed: ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var parser = new DOMParser();
        var doc    = parser.parseFromString(html, 'text/html');
        var fetched = doc.getElementById('shopify-section-' + sectionId);
        if (!fetched) return;

        sectionEl.innerHTML = fetched.innerHTML;
        initSlider(sectionEl, sectionId);
      })
      .catch(function (err) {
        sectionEl.style.display = 'none';
        if (window.console && console.warn) console.warn('[Related Products]', err);
      });
  }

  /* ── Helpers ─────────────────────────────────────────────────────────── */

  function formatMoney(cents) {
    if (!cents && cents !== 0) return '';
    return '$' + (cents / 100).toFixed(2);
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ── Slider ──────────────────────────────────────────────────────────────
     Lightweight snap-scroll carousel with:
     - Prev / Next arrow buttons
     - Dot indicator
     - Touch / mouse drag support (no deps)
  ────────────────────────────────────────────────────────────────────────── */

  function initSlider(ctx, sectionId) {
    var track   = qs('#rp-track-' + sectionId, ctx);
    var prevBtn = qs('#rp-prev-' + sectionId, ctx);
    var nextBtn = qs('#rp-next-' + sectionId, ctx);
    var dotsEl  = qs('#rp-dots-' + sectionId, ctx);

    if (!track) return;

    var cards      = qsa('.rp-card', track);
    var totalCards = cards.length;
    if (totalCards === 0) return;

    function visibleCount() {
      if (!cards[0]) return 4;
      var cardW  = cards[0].getBoundingClientRect().width;
      var trackW = track.getBoundingClientRect().width;
      return Math.max(1, Math.round(trackW / cardW));
    }

    var pageCount   = 0;
    var currentPage = 0;

    function buildDots() {
      if (!dotsEl) return;
      dotsEl.innerHTML = '';
      pageCount = Math.ceil(totalCards / visibleCount());
      if (pageCount <= 1) {
        dotsEl.style.display = 'none';
        return;
      }
      dotsEl.style.display = 'flex';
      for (var i = 0; i < pageCount; i++) {
        var dot = document.createElement('button');
        dot.className = 'rp-dot' + (i === currentPage ? ' is-active' : '');
        dot.setAttribute('aria-label', 'Go to page ' + (i + 1));
        dot.setAttribute('role', 'tab');
        dot.dataset.page = String(i);
        dot.addEventListener('click', function () {
          goToPage(parseInt(this.dataset.page, 10));
        });
        dotsEl.appendChild(dot);
      }
    }

    function updateDots() {
      if (!dotsEl) return;
      qsa('.rp-dot', dotsEl).forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === currentPage);
      });
    }

    function goToPage(page) {
      var vc = visibleCount();
      pageCount    = Math.ceil(totalCards / vc);
      currentPage  = Math.max(0, Math.min(page, pageCount - 1));
      var cardIndex  = currentPage * vc;
      var targetCard = cards[Math.min(cardIndex, totalCards - 1)];
      if (targetCard) {
        track.scrollTo({ left: targetCard.offsetLeft - 4, behavior: 'smooth' });
      }
      updateNavState();
      updateDots();
    }

    function updateNavState() {
      if (prevBtn) prevBtn.classList.toggle('is-hidden', currentPage === 0);
      if (nextBtn) nextBtn.classList.toggle('is-hidden', currentPage >= pageCount - 1);
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { goToPage(currentPage - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goToPage(currentPage + 1); });

    var scrollTimer;
    track.addEventListener('scroll', function () {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(function () {
        var vc         = visibleCount();
        var scrollLeft = track.scrollLeft;
        var newPage    = 0;
        cards.forEach(function (card, i) {
          if (card.offsetLeft - 4 <= scrollLeft + 4) {
            newPage = Math.floor(i / vc);
          }
        });
        if (newPage !== currentPage) {
          currentPage = newPage;
          updateNavState();
          updateDots();
        }
      }, 80);
    }, { passive: true });

    var isDragging      = false;
    var startX          = 0;
    var startScrollLeft = 0;

    track.addEventListener('mousedown', function (e) {
      if (e.button !== 0) return;
      isDragging = true;
      startX = e.pageX - track.offsetLeft;
      startScrollLeft = track.scrollLeft;
      track.style.scrollBehavior = 'auto';
    });

    track.addEventListener('mousemove', function (e) {
      if (!isDragging) return;
      e.preventDefault();
      var x = e.pageX - track.offsetLeft;
      track.scrollLeft = startScrollLeft - (x - startX);
    });

    function stopDrag() {
      if (!isDragging) return;
      isDragging = false;
      track.style.scrollBehavior = 'smooth';
    }

    track.addEventListener('mouseup',    stopDrag);
    track.addEventListener('mouseleave', stopDrag);

    track.addEventListener('click', function (e) {
      var delta = Math.abs(track.scrollLeft - startScrollLeft);
      if (delta > 5) e.preventDefault();
    }, true);

    buildDots();
    updateNavState();

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        buildDots();
        goToPage(0);
      }, 200);
    }, { passive: true });
  }

  /* ── Add to Cart ─────────────────────────────────────────────────────────
     Mirrors the existing ali-atc / product-atc.js behaviour so the cart
     drawer integration works seamlessly.
  ────────────────────────────────────────────────────────────────────────── */

  function initATC() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.rp-btn-cart[data-variant-id]');
      if (!btn || !btn.classList.contains('ali-atc')) return;

      e.preventDefault();
      if (btn.classList.contains('is-loading')) return;

      var variantId    = btn.dataset.variantId;
      if (!variantId) return;

      var originalHTML = btn.innerHTML;
      btn.classList.add('is-loading');
      btn.innerHTML = 'Adding\u2026';

      fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ id: variantId, quantity: 1 })
      })
        .then(function (r) {
          if (!r.ok) throw new Error('ATC failed');
          return r.json();
        })
        .then(function () {
          btn.classList.remove('is-loading');
          btn.classList.add('is-added');
          btn.innerHTML =
            '<svg width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">' +
            '<polyline points="20 6 9 17 4 12"/></svg> Added!';

          fetch('/cart.js')
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              if (typeof updateCartCount === 'function') {
                updateCartCount(cart.item_count);
              }
              var drawer  = document.getElementById('st-cart-drawer');
              var overlay = document.getElementById('st-cart-overlay');
              if (drawer)  drawer.classList.add('open');
              if (overlay) overlay.classList.add('show');
            });

          setTimeout(function () {
            btn.classList.remove('is-added');
            btn.innerHTML = originalHTML;
          }, 2200);
        })
        .catch(function () {
          btn.classList.remove('is-loading');
          btn.innerHTML = 'Error \u2014 Retry';
          setTimeout(function () { btn.innerHTML = originalHTML; }, 2000);
        });
    });
  }

  /* ── Public init ─────────────────────────────────────────────────────────
     Called from an inline <script> in the section after DOMContentLoaded.
  ────────────────────────────────────────────────────────────────────────── */

  window.rpInitSection = function (sectionId) {
    var sectionEl = document.getElementById('shopify-section-' + sectionId);
    if (!sectionEl) return;

    var hasProducts = qs('.rp-slider-track', sectionEl);
    if (hasProducts) {
      /* Products were already rendered server-side — just init slider */
      initSlider(sectionEl, sectionId);
    } else {
      /*
       * Skeleton is showing — first try to fetch from same collection,
       * then fall back to Shopify recommendations API.
       */
      fetchFromCollection(sectionEl);
    }
  };

  /* ── One-time ATC delegation ─────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initATC(); });
  } else {
    initATC();
  }

})();
